var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ErrorBoundary from "@helper/ErrorBoundary/ErrorBoundary";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { SkeletonLoader, LoadingSpinner } from "@widget/loaders";
import { TabActionsSlotContext, TabActionsCountContext } from "@widget/list/Tabs/TabSectionHeader";
import { getFontAwesomeIcon, isUserAllowed, loadAllScriptsByModuleId } from "@utils";
import { UXPContext } from "@core/context/Context";
import { NotAuthorized } from "@core/layout/components/ErrorPages";
import ErrorState from "@helper/ErrorState";
import { Dropdown } from "@widget/popups/Dropdown";
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { isObjectInfoCardProps, ObjectInfoCard } from "./ObjectInfoCardComponent";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import ActionsListComponent from "../Common/ActionListComponent";
import { ObjectDetailsPanelHeader, isObjectDetailsPanelHeaderProps } from "@widget/list/Tables/object-search/ObjectDetailsPanelHeader";
import { useRouterContext, useSearchParams } from "@hooks/useRouteHandlers";
import { buildInjectedAdditionalDetails } from "@core/pages/injected-tabs/buildInjectedAdditionalDetails";
import { COMPONENT_RESOLVE_TIMEOUT_MS } from "@helper/ComponentRenderer/ComponentRenderer";
import { registerMountedObjectType, unregisterMountedObjectType } from "@core/pages/injected-tabs/mountedInjectedTabsRegistry";
import { onInjectionRegistryChange, getInjectionsForObjectType } from "@core/pages/injected-tabs/tabInjectionRegistry";
import { registerHardcodedTabs, unregisterHardcodedTabs } from "@core/pages/injected-tabs/hardcodedTabsRegistry";
import { useIsPageEditorPreview } from "@core/context/PageEditorPreviewContext";
import { useMultiEventSubscriber } from "@realtime/eventbus/useEventBus";
import { useOverflowLayout } from "@hooks/useOverflowLayout";
import useIsMobileLayout from "@hooks/useIsMobileLayout";
import MobileToolbar from "@widget/other/MobileToolbar";
import MobileSheet from "@widget/popups/MobileSheet";
import useMobileLayer, { useHasMobileShell } from "@core/layout/MobileLayer";
import MobileBackRow from "@core/layout/MobileBackRow";
// Reserved width (px) for the "More" overflow button (tabs + toolbars).
const OVERFLOW_BTN_WIDTH = 80;
/**
 * A toolbar item that opens a dropdown. It is its own component because it needs a ref: calling
 * useRef from the toolbar render callback made the hook conditional (that callback returns early
 * while loading) and looped (it is mapped over the toolbar items), which threw React #310 as soon as
 * an item with `renderOptionsDropdown` was present.
 */
const ToolbarDropdownItem = ({ item, row, trigger }) => {
    const toolbarDropdownRef = useRef(null);
    const dropdownContent = Array.isArray(item.renderOptionsDropdown)
        ? _jsx(ActionsListComponent, { actions: item.renderOptionsDropdown, item: row, parentDropdownRef: toolbarDropdownRef })
        : item.renderOptionsDropdown;
    return _jsx(Dropdown, { ref: toolbarDropdownRef, trigger: trigger, content: dropdownContent, position: 'bottom-left' });
};
const ObjectDetailsPanelBase = forwardRef((props, ref) => {
    var _a;
    const context = useContext(UXPContext);
    const isInPreview = useIsPageEditorPreview();
    const { onClose, showCloseButton = true, title, tabs, toolbarItems, generalDetails, otherDetails, additionlDetails, data, appendToURL = true, urlParams: propUrlParams, objectType, objectKey, access, otherDetailsAccess, generalDetailsAccess } = props;
    const { navigate, generateURL } = useRouterContext();
    // Access check: `allowed` = user passes the rule; `showError` = render the
    // not-authorised error instead of hiding when denied.
    const checkAccess = useCallback((ac) => {
        // In the page editor, show all developer-configured content ungated.
        if (isInPreview)
            return { allowed: true, showError: false };
        const allowed = !ac || isUserAllowed(context, ac.userGroups || [], ac.appRoles || []);
        return { allowed, showError: !allowed && !!(ac === null || ac === void 0 ? void 0 : ac.showUnauthorizedError) };
    }, [context, isInPreview]);
    // Embedded not-authorised message for a gated section/tab (no "Go back" button).
    const renderNotAuthorized = useCallback(() => (_jsx(NotAuthorized, { mode: "inline" })), []);
    // Merge URL params with defaults
    const urlParams = useMemo(() => ({
        detailsTab: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.detailsTab) || 'dt',
        additionalDetailsTab: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.additionalDetailsTab) || 'ad'
    }), [propUrlParams]);
    const searchParams = useSearchParams();
    const dt = searchParams[urlParams.detailsTab];
    const ad = searchParams[urlParams.additionalDetailsTab];
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedAdditionlDetailsTab, setSelectedAdditionalDetailsTab] = useState(null);
    const [selectedTabId, setSelectedTabId] = useState(null);
    const [additionalDetailsRefreshKeys, setAdditionalDetailsRefreshKeys] = useState({});
    const [tabOverflowOpen, setTabOverflowOpen] = useState(false);
    // Phones: full-screen panel, tabs in the bottom toolbar, tab actions in the More sheet
    const isMobile = useIsMobileLayout();
    const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
    // Phones: where the open tab's TabSectionHeader buttons go (the bottom toolbar), and how many tabs put some there
    const [tabActionsSlot, setTabActionsSlot] = useState(null);
    const [tabHeaderCount, setTabHeaderCount] = useState(0);
    const countTabHeaders = useCallback((delta) => setTabHeaderCount(n => n + delta), []);
    // Web: a side tab's TabSectionHeader buttons show as icons in its header, next to refresh
    const [gutterActionsSlot, setGutterActionsSlot] = useState(null);
    // An action's own options (renderOptionsDropdown), shown in a second sheet
    const [mobileOptions, setMobileOptions] = useState(null);
    const tabOverflowDropdownRef = useRef(null);
    const [toolbarLeftOverflowFrom, setToolbarLeftOverflowFrom] = useState(null);
    const [toolbarRightOverflowFrom, setToolbarRightOverflowFrom] = useState(null);
    const [toolbarLeftOverflowOpen, setToolbarLeftOverflowOpen] = useState(false);
    const [toolbarRightOverflowOpen, setToolbarRightOverflowOpen] = useState(false);
    const toolbarLeftContainerRef = useRef(null);
    const toolbarRightContainerRef = useRef(null);
    const toolbarLeftItemRefs = useRef(new Map());
    const toolbarRightItemRefs = useRef(new Map());
    const toolbarLeftWidthCache = useRef(new Map());
    const toolbarRightWidthCache = useRef(new Map());
    const toolbarLeftOverflowDropdownRef = useRef(null);
    const toolbarRightOverflowDropdownRef = useRef(null);
    // Normalize old API to new tab structure
    const effectiveTabs = useMemo(() => {
        const rawTabs = tabs
            ? tabs
            // Old API: create single implicit tab (gated via generalDetailsAccess)
            : [{
                    id: 'general',
                    toolbarItems,
                    details: generalDetails,
                    access: generalDetailsAccess,
                }];
        // Drop tabs the user can't access, unless they opted to show the error.
        return rawTabs.filter(t => {
            const { allowed, showError } = checkAccess(t.access);
            return allowed || showError;
        });
    }, [tabs, toolbarItems, generalDetails, generalDetailsAccess, checkAccess]);
    const shouldShowTabs = effectiveTabs.length > 1;
    const activeTab = useMemo(() => {
        // Priority: URL parameter (if appendToURL) > selectedTabId > first tab
        const tabId = (appendToURL && dt) ? dt : selectedTabId;
        if (!tabId && effectiveTabs.length > 0) {
            return effectiveTabs[0];
        }
        // Validate tab ID exists
        const foundTab = effectiveTabs.find(t => t.id === tabId);
        return foundTab || effectiveTabs[0];
    }, [selectedTabId, effectiveTabs, appendToURL, dt]);
    // Tab overflow: collapse tabs that don't fit into a "More" dropdown, promoting
    // the active tab out of the dropdown so it's always shown in full.
    const { containerRef: tabsContainerRef, setItemRef: setTabRef, orderedItems: orderedTabs, isHidden: isTabHidden, overflowItems: overflowTabs, hasOverflow: hasTabOverflow, visibleCount: tabVisibleCount, } = useOverflowLayout(effectiveTabs, (_a = activeTab === null || activeTab === void 0 ? void 0 : activeTab.id) !== null && _a !== void 0 ? _a : null, { overflowButtonWidth: OVERFLOW_BTN_WIDTH });
    // Resolve toolbar items (static or function-based)
    const resolvedToolbarItems = useMemo(() => {
        if (!(activeTab === null || activeTab === void 0 ? void 0 : activeTab.toolbarItems))
            return null;
        if (typeof activeTab.toolbarItems === 'function') {
            return row ? activeTab.toolbarItems(row) : null;
        }
        return activeTab.toolbarItems;
    }, [activeTab, row]);
    // Injected tabs render from the runtime registry; re-read on each registration so late
    // registrations (after their app bundle loads) appear.
    const [registryVersion, setRegistryVersion] = useState(0);
    useEffect(() => {
        if (!objectType)
            return;
        const unsubscribe = onInjectionRegistryChange(() => setRegistryVersion(v => v + 1));
        setRegistryVersion(v => v + 1); // catch registrations that landed before this effect attached
        return unsubscribe;
    }, [objectType]);
    const injectedDetails = useMemo(() => {
        var _a;
        if (!objectType)
            return [];
        return buildInjectedAdditionalDetails({
            objectTabs: (context === null || context === void 0 ? void 0 : context.objectTabs) || {},
            objectType,
            objectKey,
            parentProps: props,
            userAppRoles: (context === null || context === void 0 ? void 0 : context.appRoles) || {},
            userGroupKey: ((_a = context === null || context === void 0 ? void 0 : context.userDetails) === null || _a === void 0 ? void 0 : _a.UserGroupKey) || '',
        });
    }, [objectType, objectKey, context === null || context === void 0 ? void 0 : context.objectTabs, context === null || context === void 0 ? void 0 : context.appRoles, context === null || context === void 0 ? void 0 : context.userDetails, registryVersion]);
    // Preload each declared tab's component bundle up front. This triggers the injecting app to
    // load and call registerTabInjection — which resolves the loading state below.
    useEffect(() => {
        var _a, _b;
        if (!objectType)
            return;
        const key = objectType.toLowerCase();
        const tabs = [...(((_a = context === null || context === void 0 ? void 0 : context.objectTabs) === null || _a === void 0 ? void 0 : _a[key]) || []), ...(((_b = context === null || context === void 0 ? void 0 : context.declaredObjectTabs) === null || _b === void 0 ? void 0 : _b[key]) || [])];
        const moduleIds = new Set();
        tabs.forEach(t => (t.components || []).forEach(c => {
            var _a;
            const moduleId = (_a = c.componentId) === null || _a === void 0 ? void 0 : _a.split('/')[0];
            if (moduleId)
                moduleIds.add(moduleId);
        }));
        moduleIds.forEach(m => { loadAllScriptsByModuleId(context, m).catch(() => { }); });
    }, [objectType, context === null || context === void 0 ? void 0 : context.objectTabs, context === null || context === void 0 ? void 0 : context.declaredObjectTabs]);
    // Deterministic loading state: the bundle.json manifest says which injected tabs to expect
    // for this object type; show ONE gutter spinner until every expected tab has registered at
    // runtime. No declared tabs → no spinner. Capped so a script that never registers can't
    // spin forever.
    const [injectionWaitedOut, setInjectionWaitedOut] = useState(false);
    useEffect(() => {
        if (!objectType)
            return;
        setInjectionWaitedOut(false);
        const t = setTimeout(() => setInjectionWaitedOut(true), COMPONENT_RESOLVE_TIMEOUT_MS);
        return () => clearTimeout(t);
    }, [objectType]);
    const injectionsLoading = useMemo(() => {
        var _a;
        if (injectionWaitedOut)
            return false;
        const key = objectType === null || objectType === void 0 ? void 0 : objectType.toLowerCase();
        if (!key)
            return false;
        // Expected = every declared tab; a DB record only customises a registration, it
        // does not stand in for one (see buildInjectedAdditionalDetails).
        const expected = (((_a = context === null || context === void 0 ? void 0 : context.declaredObjectTabs) === null || _a === void 0 ? void 0 : _a[key]) || [])
            .map(t => { var _a; return (_a = t.contributionId) === null || _a === void 0 ? void 0 : _a.toLowerCase(); })
            .filter(Boolean);
        if (expected.length === 0)
            return false;
        const registered = new Set(getInjectionsForObjectType(key).map(i => i.id.toLowerCase()));
        return expected.some(id => !registered.has(id));
    }, [objectType, context === null || context === void 0 ? void 0 : context.declaredObjectTabs, registryVersion, injectionWaitedOut]);
    // Merge host-declared additional details with injected ones. Host tabs are
    // filtered by their access rule (drop denied+hide; keep denied+showError to
    // error on open); injected tabs are already role-filtered upstream.
    const allAdditionalDetails = useMemo(() => {
        const host = (additionlDetails || []).filter(t => {
            const { allowed, showError } = checkAccess(t.access);
            return allowed || showError;
        });
        return [...host, ...injectedDetails];
    }, [additionlDetails, injectedDetails, checkAccess]);
    // Build event → tab id mapping for auto-refresh subscriptions
    const eventTabMap = useMemo(() => {
        const map = {};
        allAdditionalDetails.forEach(tab => {
            var _a;
            (_a = tab.events) === null || _a === void 0 ? void 0 : _a.forEach(ev => {
                var _a;
                ((_a = map[ev]) !== null && _a !== void 0 ? _a : (map[ev] = [])).push(tab.id);
            });
        });
        return map;
    }, [allAdditionalDetails]);
    const allTabEvents = useMemo(() => Object.keys(eventTabMap), [eventTabMap]);
    useMultiEventSubscriber('uxp-obsdp', {
        events: allTabEvents,
        sharedCallback: (_payload, eventName) => {
            var _a;
            const tabIds = (_a = eventTabMap[eventName]) !== null && _a !== void 0 ? _a : [];
            if (tabIds.length === 0)
                return;
            setAdditionalDetailsRefreshKeys(prev => {
                const next = Object.assign({}, prev);
                tabIds.forEach(id => { next[id] = (next[id] || 0) + 1; });
                return next;
            });
        }
    });
    // Register / unregister this objectType in the mounted registry so ConfiguredPage
    // can detect that this page has injectable tab slots
    useEffect(() => {
        if (!objectType || isInPreview)
            return;
        registerMountedObjectType(objectType);
        return () => unregisterMountedObjectType(objectType);
    }, [objectType, isInPreview]);
    // Register hardcoded tabs so the page editor preview can include them in the gutter
    useEffect(() => {
        if (!objectType || !(additionlDetails === null || additionlDetails === void 0 ? void 0 : additionlDetails.length) || isInPreview)
            return;
        registerHardcodedTabs(objectType, additionlDetails.map(t => {
            var _a, _b;
            return ({
                id: t.id,
                icon: t.icon,
                label: t.label,
                // Surface the developer's access config read-only in the page editor.
                appRoles: (_a = t.access) === null || _a === void 0 ? void 0 : _a.appRoles,
                userGroups: (_b = t.access) === null || _b === void 0 ? void 0 : _b.userGroups,
            });
        }));
        return () => unregisterHardcodedTabs(objectType);
    }, [objectType, additionlDetails, isInPreview]);
    // Access for the whole page and the "other details" section.
    const pageAccess = checkAccess(access);
    const otherDetailsCheck = checkAccess(otherDetailsAccess);
    const hasAdditionalDetails = allAdditionalDetails.length > 0;
    // Other details shows only when present and either allowed or opted into the error.
    const hasOtherDetails = (!!otherDetails) && (otherDetailsCheck.allowed || otherDetailsCheck.showError);
    const hasGeneralDetails = (!!(activeTab === null || activeTab === void 0 ? void 0 : activeTab.details));
    const hasToolbarItems = (((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.left) && resolvedToolbarItems.left.length > 0) || ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.right) && resolvedToolbarItems.right.length > 0));
    const loadData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            let rowData;
            if (typeof data === 'function') {
                rowData = yield data();
            }
            else {
                rowData = data;
            }
            setRow(rowData);
        }
        catch (error) {
            console.error("Failed to load row data:", error);
            setRow(null); // Handle error state as needed
        }
        finally {
            setLoading(false);
        }
    }), [data]);
    useImperativeHandle(ref, () => ({
        refresh: () => {
            loadData();
        }
    }));
    useEffect(() => {
        loadData();
    }, [loadData]);
    const renderDetails = useCallback((details) => {
        if (!row)
            return null;
        if (typeof details === 'function')
            return details(row, loading);
        return details;
    }, [row, loading]);
    const renderGeneralDetails = useCallback(() => {
        // Active tab only reaches here when denied if it opted into the error.
        if (!checkAccess(activeTab === null || activeTab === void 0 ? void 0 : activeTab.access).allowed)
            return renderNotAuthorized();
        if (!row && !loading)
            return _jsx("div", { children: "Data not available" });
        if (!(activeTab === null || activeTab === void 0 ? void 0 : activeTab.details))
            return null;
        const details = activeTab.details;
        if (typeof details === 'function') {
            const res = details(row);
            if (isObjectInfoCardProps(res))
                return _jsx(ObjectInfoCard, Object.assign({}, res, (isMobile ? { columns: 1 } : {}), { loading: loading, layout: 'vertical' // TODO:: change based on container size may be
                 }));
            return res;
        }
        return details;
    }, [row, loading, activeTab, checkAccess, renderNotAuthorized, isMobile]);
    const renderTitle = useCallback(() => {
        if (typeof title === 'function') {
            const res = title(row, loading);
            if (isObjectDetailsPanelHeaderProps(res)) {
                return _jsx(ObjectDetailsPanelHeader, Object.assign({}, res, { item: row, loading: loading }));
            }
            return res;
        }
        if (isObjectDetailsPanelHeaderProps(title)) {
            return _jsx(ObjectDetailsPanelHeader, Object.assign({}, title, { item: row, loading: loading }));
        }
        return renderDetails(title);
    }, [row, loading, title, renderDetails]);
    // Phones: the object's name goes in the app header (the page that opened the panel owns Close)
    const mobileTitle = useMemo(() => {
        if (!isMobile || !row)
            return undefined;
        let res = typeof title === 'function' ? title(row, loading) : title;
        if (isObjectDetailsPanelHeaderProps(res))
            res = typeof res.title === 'function' ? res.title(row, loading) : res.title;
        return typeof res === 'string' ? res : undefined;
    }, [isMobile, row, loading, title]);
    useMobileLayer(!!mobileTitle, { title: mobileTitle });
    // Outside the mobile shell there is no Close bar: the layer gets its own back row
    const hasShell = useHasMobileShell();
    const renderNotFound = useCallback((message) => {
        return _jsx("div", { className: "uxpcore_obsdp__additional_details_notfound", children: _jsx(ErrorState, { mode: "inline", variant: "warning", title: message }) });
    }, []);
    const activeAdditionalDetailsTab = useMemo(() => {
        if (!hasAdditionalDetails)
            return null;
        const tabId = (appendToURL && ad) ? ad : selectedAdditionlDetailsTab;
        if (!tabId)
            return null;
        const found = allAdditionalDetails.find(t => t.id === tabId);
        return found ? found.id : null;
    }, [appendToURL, ad, selectedAdditionlDetailsTab, allAdditionalDetails, hasAdditionalDetails]);
    const isAdditionalDetailsPanelOpen = !!activeAdditionalDetailsTab;
    const renderAdditionalDetails = useCallback(() => {
        if (!row && !loading)
            return renderNotFound(context.$L('uxp-core.text.data-not-available'));
        if (!activeAdditionalDetailsTab && !loading)
            return renderNotFound(context.$L('uxp-core.text.select-a-tab'));
        const additionalDetailsTab = allAdditionalDetails.find((ad) => ad.id === activeAdditionalDetailsTab);
        if (!additionalDetailsTab && !loading)
            return renderNotFound(context.$L('uxp-core.text.selected-tab-not-found'));
        return _jsxs(_Fragment, { children: [_jsxs("div", { className: "uxpcore_obsdp__additional_details_header", children: [_jsx("div", { className: "uxpcore_obsdp__additional_details_header-icon", children: loading
                                ? _jsx(SkeletonLoader, { width: "1rem", height: "1rem", additionalStyle: { borderRadius: '1rem' } })
                                : _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(additionalDetailsTab === null || additionalDetailsTab === void 0 ? void 0 : additionalDetailsTab.icon) }) }), _jsx("div", { className: "uxpcore_obsdp__additional_details_header-label", children: loading
                                ? _jsx(SkeletonLoader, { width: "5rem", height: "1rem" })
                                : additionalDetailsTab === null || additionalDetailsTab === void 0 ? void 0 : additionalDetailsTab.label }), _jsx("div", { className: "uxpcore_obsdp__additional_details_header-actions", ref: setGutterActionsSlot }), (additionalDetailsTab === null || additionalDetailsTab === void 0 ? void 0 : additionalDetailsTab.refreshable) && !loading && (_jsx("div", { className: "uxpcore_obsdp__additional_details_header-refresh", onClick: () => setAdditionalDetailsRefreshKeys(prev => (Object.assign(Object.assign({}, prev), { [activeAdditionalDetailsTab]: (prev[activeAdditionalDetailsTab] || 0) + 1 }))), children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon('fas rotate-right') }) }))] }), _jsx("div", { className: "uxpcore_obsdp__additional_details_body", children: _jsx(TabActionsSlotContext.Provider, { value: isMobile ? tabActionsSlot : gutterActionsSlot, children: _jsx(ErrorBoundary, { children: checkAccess(additionalDetailsTab === null || additionalDetailsTab === void 0 ? void 0 : additionalDetailsTab.access).allowed
                                ? renderDetails(additionalDetailsTab === null || additionalDetailsTab === void 0 ? void 0 : additionalDetailsTab.content)
                                : renderNotAuthorized() }) }) }, additionalDetailsRefreshKeys[activeAdditionalDetailsTab] || 0)] });
    }, [allAdditionalDetails, activeAdditionalDetailsTab, renderDetails, loading, row, additionalDetailsRefreshKeys, checkAccess, renderNotAuthorized, gutterActionsSlot, tabActionsSlot, isMobile]);
    const renderToolbarItem = useCallback((item, key, showLabel = true) => {
        if (loading) {
            return _jsx("div", { className: "uxpcore_obsdp__toolbar_item", children: showLabel
                    ? _jsx(SkeletonLoader, { width: "5rem", height: "1.5rem" })
                    : _jsx(SkeletonLoader, { width: "1.5rem", height: "1.5rem", additionalStyle: { borderRadius: '1rem' } }) }, key);
        }
        function renderToolbarButton() {
            return _jsxs("div", { className: "uxpcore_obsdp__toolbar_item", onClick: (e) => {
                    if (!!(item === null || item === void 0 ? void 0 : item.renderOptionsDropdown))
                        return;
                    if (row && (item === null || item === void 0 ? void 0 : item.onClick))
                        item.onClick(e, row);
                }, children: [_jsx("div", { className: "uxpcore_obsdp__toolbar_item-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(item.icon) }) }), showLabel
                        ? _jsx("div", { className: "uxpcore_obsdp__toolbar_item-label", children: context.$L(item.label) })
                        : null] }, key);
        }
        if (!!(item === null || item === void 0 ? void 0 : item.renderOptionsDropdown)) {
            return _jsx(ToolbarDropdownItem, { item: item, row: row, trigger: renderToolbarButton() }, key);
        }
        return renderToolbarButton();
    }, [row, loading]);
    const measureToolbarLeftOverflow = useCallback(() => {
        const items = ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.left) || []).filter(t => !t.visible || (row && t.visible(row)));
        setToolbarLeftOverflowFrom(null);
        requestAnimationFrame(() => {
            var _a, _b, _c;
            items.forEach((_, i) => {
                const el = toolbarLeftItemRefs.current.get(String(i));
                if (el && el.offsetWidth > 0)
                    toolbarLeftWidthCache.current.set(String(i), el.offsetWidth);
            });
            const wrapperWidth = (_b = (_a = toolbarLeftContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
            if (wrapperWidth === 0)
                return;
            const total = items.reduce((s, _, i) => { var _a; return s + ((_a = toolbarLeftWidthCache.current.get(String(i))) !== null && _a !== void 0 ? _a : 0); }, 0);
            if (total <= wrapperWidth) {
                setToolbarLeftOverflowFrom(null);
                return;
            }
            let acc = 0;
            for (let i = 0; i < items.length; i++) {
                const w = (_c = toolbarLeftWidthCache.current.get(String(i))) !== null && _c !== void 0 ? _c : 0;
                if (acc + w + OVERFLOW_BTN_WIDTH > wrapperWidth) {
                    setToolbarLeftOverflowFrom(i);
                    return;
                }
                acc += w;
            }
            setToolbarLeftOverflowFrom(null);
        });
    }, [resolvedToolbarItems, row]);
    const measureToolbarRightOverflow = useCallback(() => {
        const items = ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.right) || []).filter(t => !t.visible || (row && t.visible(row)));
        setToolbarRightOverflowFrom(null);
        requestAnimationFrame(() => {
            var _a, _b, _c;
            items.forEach((_, i) => {
                const el = toolbarRightItemRefs.current.get(String(i));
                if (el && el.offsetWidth > 0)
                    toolbarRightWidthCache.current.set(String(i), el.offsetWidth);
            });
            const wrapperWidth = (_b = (_a = toolbarRightContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
            if (wrapperWidth === 0)
                return;
            const total = items.reduce((s, _, i) => { var _a; return s + ((_a = toolbarRightWidthCache.current.get(String(i))) !== null && _a !== void 0 ? _a : 0); }, 0);
            if (total <= wrapperWidth) {
                setToolbarRightOverflowFrom(null);
                return;
            }
            let acc = 0;
            for (let i = 0; i < items.length; i++) {
                const w = (_c = toolbarRightWidthCache.current.get(String(i))) !== null && _c !== void 0 ? _c : 0;
                if (acc + w + OVERFLOW_BTN_WIDTH > wrapperWidth) {
                    setToolbarRightOverflowFrom(i);
                    return;
                }
                acc += w;
            }
            setToolbarRightOverflowFrom(null);
        });
    }, [resolvedToolbarItems, row]);
    useEffect(() => { measureToolbarLeftOverflow(); measureToolbarRightOverflow(); }, [resolvedToolbarItems, row]);
    useEffect(() => {
        window.addEventListener('resize', measureToolbarLeftOverflow);
        window.addEventListener('resize', measureToolbarRightOverflow);
        return () => {
            window.removeEventListener('resize', measureToolbarLeftOverflow);
            window.removeEventListener('resize', measureToolbarRightOverflow);
        };
    }, [measureToolbarLeftOverflow, measureToolbarRightOverflow]);
    const renderTabNavigation = useCallback(() => {
        if (!shouldShowTabs)
            return null;
        const handleTabClick = (tabId) => {
            var _a;
            (_a = tabOverflowDropdownRef.current) === null || _a === void 0 ? void 0 : _a.close();
            setTabOverflowOpen(false);
            if (appendToURL) {
                navigate(generateURL({ searchParams: { [urlParams.detailsTab]: tabId } }));
            }
            else {
                setSelectedTabId(tabId);
            }
        };
        return (_jsxs("div", { ref: tabsContainerRef, className: "uxpcore_obsdp__tabs_container", children: [orderedTabs.map((tab) => (_jsxs("div", { ref: setTabRef(tab.id), className: classNames('uxpcore_obsdp__tab', { 'uxpcore_obsdp__tab--active': (activeTab === null || activeTab === void 0 ? void 0 : activeTab.id) === tab.id }, { 'uxpcore_obsdp__tab--hidden': isTabHidden(tab.id) }), onClick: () => handleTabClick(tab.id), children: [tab.icon && (_jsx("div", { className: "uxpcore_obsdp__tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(tab.icon) }) })), tab.label && (_jsx("div", { className: "uxpcore_obsdp__tab-label", children: tab.label }))] }, tab.id))), hasTabOverflow && (_jsx(Dropdown, { ref: tabOverflowDropdownRef, trigger: _jsxs("div", { className: classNames('uxpcore_obsdp__tab', { 'uxpcore_obsdp__tab--active': tabVisibleCount === 0 }), children: [tabVisibleCount === 0 && (activeTab === null || activeTab === void 0 ? void 0 : activeTab.label) ? activeTab.label : context.$L('uxp-core.text.more'), _jsx(DropdownIndicator, { isOpen: tabOverflowOpen })] }), isOpen: tabOverflowOpen, onToggle: () => setTabOverflowOpen(p => !p), content: _jsx("div", { className: "uxpcore_obsdp__overflow_menu", children: overflowTabs.map(tab => (_jsxs("div", { className: classNames('uxpcore_obsdp__overflow_item', { 'uxpcore_obsdp__overflow_item--active': (activeTab === null || activeTab === void 0 ? void 0 : activeTab.id) === tab.id }), onClick: () => handleTabClick(tab.id), children: [tab.icon && (_jsx("div", { className: "uxpcore_obsdp__tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(tab.icon) }) })), tab.label && (_jsx("div", { className: "uxpcore_obsdp__tab-label", children: tab.label }))] }, tab.id))) }), position: "bottom-left" }))] }));
    }, [shouldShowTabs, activeTab, appendToURL, navigate, generateURL, urlParams, tabOverflowOpen, orderedTabs, overflowTabs, hasTabOverflow, isTabHidden, setTabRef, tabsContainerRef, tabVisibleCount, context]);
    // Page-level access: denied users get the not-authorised error (if opted in)
    // or an empty panel; the close button is kept so they can dismiss it.
    if (!pageAccess.allowed) {
        return _jsxs("div", { className: "uxpcore_obsdp__container", children: [_jsx("div", { className: "uxpcore_obsdp__content_container", children: pageAccess.showError && _jsx(NotAuthorized, {}) }), showCloseButton && (_jsx("div", { className: "uxpcore_obsdp__gutter_container", children: _jsx("div", { className: "uxpcore_obsdp__close_btn", onClick: (e) => { e.preventDefault(); e.stopPropagation(); onClose === null || onClose === void 0 ? void 0 : onClose(); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'times'] }) }) }))] });
    }
    if (isMobile) {
        const visible = (t) => !t.visible || (row && t.visible(row));
        const tabActions = [...((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.left) || []), ...((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.right) || [])].filter(visible);
        const activeGutterTab = allAdditionalDetails.find(t => t.id === activeAdditionalDetailsTab);
        // One slider for the main tabs ("Details") and the gutter tabs ("More")
        const both = effectiveTabs.length > 0 && allAdditionalDetails.length > 0;
        const tabItems = [
            ...effectiveTabs.map(t => ({ id: `t:${t.id}`, label: t.label || '', icon: t.icon, group: both ? context.$L('uxp-core.text.details') : undefined })),
            ...allAdditionalDetails.map(t => ({ id: `a:${t.id}`, label: t.label, icon: t.icon, group: both ? context.$L('uxp-core.text.more') : undefined })),
        ];
        const selectTab = (id) => {
            const tabId = id.slice(2);
            if (id.startsWith('t:')) {
                if (appendToURL)
                    navigate(generateURL({ searchParams: { [urlParams.detailsTab]: tabId }, removeParams: [urlParams.additionalDetailsTab] }));
                else {
                    setSelectedTabId(tabId);
                    setSelectedAdditionalDetailsTab(null);
                }
            }
            else {
                if (appendToURL)
                    navigate(generateURL({ searchParams: { [urlParams.additionalDetailsTab]: tabId } }));
                else
                    setSelectedAdditionalDetailsTab(tabId);
            }
        };
        const runAction = (item, e) => {
            setMobileMoreOpen(false);
            if (item.renderOptionsDropdown)
                setMobileOptions({ item, open: true });
            else if (row && item.onClick)
                item.onClick(e, row);
        };
        const refreshGutterTab = () => {
            setMobileMoreOpen(false);
            setAdditionalDetailsRefreshKeys(prev => (Object.assign(Object.assign({}, prev), { [activeAdditionalDetailsTab]: (prev[activeAdditionalDetailsTab] || 0) + 1 })));
        };
        // The open tab's actions: up to 4 sit in the toolbar as icons (label on hold); more than 4
        // show the first 3 and a ⋯ with the rest
        const mobileActions = activeGutterTab
            ? (activeGutterTab.refreshable ? [{ icon: 'fas rotate-right', label: context.$L('uxp-core.text.refresh'), run: refreshGutterTab }] : [])
            : tabActions.map(item => ({ icon: item.icon, label: context.$L(item.label), hasOptions: !!item.renderOptionsDropdown, run: (e) => runAction(item, e) }));
        const MAX_INLINE = 4;
        const inlineActions = mobileActions.length <= MAX_INLINE ? mobileActions : mobileActions.slice(0, MAX_INLINE - 1);
        const moreRows = mobileActions.slice(inlineActions.length).map((a, i) => _jsxs("button", { className: "uxpcore_sheetlist__row", onClick: (e) => a.run(e), children: [_jsx("span", { className: "uxpcore_sheetlist__icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(a.icon) }) }), _jsx("span", { className: "uxpcore_sheetlist__label", children: a.label }), a.hasOptions && _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'chevron-right'] })] }, i));
        const options = mobileOptions === null || mobileOptions === void 0 ? void 0 : mobileOptions.item.renderOptionsDropdown;
        // A full-screen layer down to the footer. It starts under the app header (a see-through
        // spacer); scrolling up slides the head over the header, where it sticks.
        return createPortal(_jsxs("div", { className: "uxpcore_obsdp__container uxpcore_obsdp__container--mobile", children: [!hasShell && onClose && _jsx(MobileBackRow, { title: mobileTitle || context.$L('uxp-core.text.back'), onBack: () => onClose() }), _jsxs("div", { className: "uxpcore_obsdp__mscroll", children: [_jsx("div", { className: "uxpcore_obsdp__mspacer" }), _jsx("div", { className: "uxpcore_obsdp__titile_container uxpcore_obsdp__mhead", children: _jsx(ErrorBoundary, { children: renderTitle() }) }), _jsx("div", { className: "uxpcore_obsdp__mbody", children: _jsx(TabActionsSlotContext.Provider, { value: tabActionsSlot, children: _jsx(TabActionsCountContext.Provider, { value: countTabHeaders, children: activeGutterTab
                                        ? _jsx("div", { className: "uxpcore_obsdp__mgutter", children: renderAdditionalDetails() })
                                        : _jsxs(_Fragment, { children: [hasGeneralDetails && _jsx("div", { className: classNames("uxpcore_obsdp__general_details_container", { "uxpcore_obsdp__general_details_container--fill": !hasOtherDetails }), children: _jsx(ErrorBoundary, { children: renderGeneralDetails() }) }), hasOtherDetails && _jsx("div", { className: "uxpcore_obsdp__other_details_container", children: _jsx(ErrorBoundary, { children: otherDetailsCheck.allowed ? renderDetails(otherDetails) : renderNotAuthorized() }) })] }) }) }) })] }), (tabItems.length > 1 || mobileActions.length > 0 || tabHeaderCount > 0) && _jsx(MobileToolbar, { items: tabItems.length > 1 ? tabItems : [], selected: activeGutterTab ? `a:${activeGutterTab.id}` : `t:${activeTab === null || activeTab === void 0 ? void 0 : activeTab.id}`, onSelect: selectTab, actions: [
                        ...inlineActions.map((a, i) => ({ id: `action-${i}`, icon: a.icon, label: a.label, onClick: () => a.run() })),
                        ...(moreRows.length > 0 ? [{
                                id: 'more', icon: 'phr dots-three', label: context.$L('uxp-core.text.actions'),
                                active: mobileMoreOpen, onClick: () => setMobileMoreOpen(o => !o)
                            }] : []),
                    ], 
                    // The open tab's own buttons (TabSectionHeader), as icons after the panel's
                    // ponytail: icons only; a tab with three or more buttons would crowd the bar, add a ⋯ then
                    trailing: _jsx("div", { className: "uxpcore_mobiletoolbar__host", ref: setTabActionsSlot }) }), _jsx(MobileSheet, { isOpen: mobileMoreOpen, onClose: () => setMobileMoreOpen(false), children: _jsx("div", { className: "uxpcore_sheetlist", children: moreRows }) }), _jsx(MobileSheet, { isOpen: !!(mobileOptions === null || mobileOptions === void 0 ? void 0 : mobileOptions.open), onClose: () => setMobileOptions(o => o && Object.assign(Object.assign({}, o), { open: false })), title: mobileOptions ? context.$L(mobileOptions.item.label) : undefined, children: _jsx("div", { className: "uxpcore_sheetlist", children: Array.isArray(options)
                            ? _jsx(ActionsListComponent, { actions: options, item: row, parentDropdownRef: { current: { close: () => setMobileOptions(o => o && Object.assign(Object.assign({}, o), { open: false })) } } })
                            : options }) })] }), document.body);
    }
    return _jsxs("div", { className: "uxpcore_obsdp__container", children: [_jsxs("div", { className: "uxpcore_obsdp__content_container", children: [_jsx("div", { className: classNames("uxpcore_obsdp__titile_container", {
                            'uxpcore_obsdp__titile_container--title-only': typeof title === 'string'
                        }), children: _jsx(ErrorBoundary, { children: renderTitle() }) }), renderTabNavigation(), hasToolbarItems && (() => {
                        const visibleLeftItems = ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.left) || []).filter(t => !t.visible || (row && t.visible(row)));
                        const visibleRightItems = ((resolvedToolbarItems === null || resolvedToolbarItems === void 0 ? void 0 : resolvedToolbarItems.right) || []).filter(t => !t.visible || (row && t.visible(row)));
                        const leftOverflowItems = toolbarLeftOverflowFrom !== null ? visibleLeftItems.slice(toolbarLeftOverflowFrom) : [];
                        const rightOverflowItems = toolbarRightOverflowFrom !== null ? visibleRightItems.slice(toolbarRightOverflowFrom) : [];
                        return (_jsxs("div", { className: "uxpcore_obsdp__toolbar_container", children: [_jsxs("div", { ref: toolbarLeftContainerRef, className: "uxpcore_obsdp__toolbar_items uxpcore_obsdp__toolbar_items-left", children: [visibleLeftItems.map((t, i) => (_jsx("div", { ref: el => { if (el)
                                                toolbarLeftItemRefs.current.set(String(i), el); }, className: classNames('uxpcore_obsdp__toolbar_item_wrapper', {
                                                'uxpcore_obsdp__toolbar_item_wrapper--hidden': toolbarLeftOverflowFrom !== null && i >= toolbarLeftOverflowFrom
                                            }), children: renderToolbarItem(t, i) }, i))), leftOverflowItems.length > 0 && (_jsx(Dropdown, { ref: toolbarLeftOverflowDropdownRef, trigger: _jsx("div", { className: "uxpcore_obsdp__toolbar_item uxpcore_obsdp__toolbar_overflow_btn", children: _jsx(DropdownIndicator, { isOpen: toolbarLeftOverflowOpen }) }), isOpen: toolbarLeftOverflowOpen, onToggle: () => setToolbarLeftOverflowOpen(p => !p), content: _jsx("div", { className: "uxpcore_obsdp__overflow_menu", children: leftOverflowItems.map((item, i) => (_jsxs("div", { className: "uxpcore_obsdp__overflow_item", onClick: (e) => {
                                                        var _a, _b;
                                                        (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, e, row);
                                                        (_b = toolbarLeftOverflowDropdownRef.current) === null || _b === void 0 ? void 0 : _b.close();
                                                        setToolbarLeftOverflowOpen(false);
                                                    }, children: [_jsx("div", { className: "uxpcore_obsdp__tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(item.icon) }) }), _jsx("div", { className: "uxpcore_obsdp__tab-label", children: context.$L(item.label) })] }, i))) }), position: "bottom-left" }))] }), _jsxs("div", { ref: toolbarRightContainerRef, className: "uxpcore_obsdp__toolbar_items uxpcore_obsdp__toolbar_items-right", children: [visibleRightItems.map((t, i) => (_jsx("div", { ref: el => { if (el)
                                                toolbarRightItemRefs.current.set(String(i), el); }, className: classNames('uxpcore_obsdp__toolbar_item_wrapper', {
                                                'uxpcore_obsdp__toolbar_item_wrapper--hidden': toolbarRightOverflowFrom !== null && i >= toolbarRightOverflowFrom
                                            }), children: renderToolbarItem(t, i, false) }, i))), rightOverflowItems.length > 0 && (_jsx(Dropdown, { ref: toolbarRightOverflowDropdownRef, trigger: _jsx("div", { className: "uxpcore_obsdp__toolbar_item uxpcore_obsdp__toolbar_overflow_btn", children: _jsx(DropdownIndicator, { isOpen: toolbarRightOverflowOpen }) }), isOpen: toolbarRightOverflowOpen, onToggle: () => setToolbarRightOverflowOpen(p => !p), content: _jsx("div", { className: "uxpcore_obsdp__overflow_menu", children: rightOverflowItems.map((item, i) => (_jsxs("div", { className: "uxpcore_obsdp__overflow_item", onClick: (e) => {
                                                        var _a, _b;
                                                        (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, e, row);
                                                        (_b = toolbarRightOverflowDropdownRef.current) === null || _b === void 0 ? void 0 : _b.close();
                                                        setToolbarRightOverflowOpen(false);
                                                    }, children: [_jsx("div", { className: "uxpcore_obsdp__tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(item.icon) }) }), _jsx("div", { className: "uxpcore_obsdp__tab-label", children: context.$L(item.label) })] }, i))) }), position: "bottom-right" }))] })] }));
                    })(), _jsxs("div", { className: "uxpcore_obsdp__details_container", children: [_jsxs("div", { className: classNames("uxpcore_obsdp__main_details_container", {
                                    "uxpcore_obsdp__main_details_container--other-only": !hasGeneralDetails && hasOtherDetails
                                }), children: [hasGeneralDetails && (_jsx("div", { className: classNames("uxpcore_obsdp__general_details_container", { "uxpcore_obsdp__general_details_container--fill": !hasOtherDetails }), children: _jsx(ErrorBoundary, { children: renderGeneralDetails() }) })), hasOtherDetails && (_jsx("div", { className: classNames("uxpcore_obsdp__other_details_container", {
                                            "uxpcore_obsdp__other_details_container--full-height": !hasGeneralDetails
                                        }), children: _jsx(ErrorBoundary, { children: otherDetailsCheck.allowed ? renderDetails(otherDetails) : renderNotAuthorized() }) }))] }), _jsx("div", { className: classNames("uxpcore_obsdp__additional_details_container", {
                                    "uxpcore_obsdp__additional_details_container--open": isAdditionalDetailsPanelOpen
                                }), children: isAdditionalDetailsPanelOpen && renderAdditionalDetails() })] })] }), (showCloseButton || hasAdditionalDetails || injectionsLoading)
                ? _jsxs("div", { className: "uxpcore_obsdp__gutter_container", children: [showCloseButton ?
                            _jsx("div", { className: "uxpcore_obsdp__close_btn", onClick: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClose === null || onClose === void 0 ? void 0 : onClose();
                                }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'times'] }) })
                            : null, _jsxs("div", { className: "uxpcore_obsdp__additional_details_tabs", children: [allAdditionalDetails.map((adTab, i) => {
                                    const isActive = activeAdditionalDetailsTab === adTab.id && isAdditionalDetailsPanelOpen;
                                    return _jsx("div", { className: classNames("uxpcore_obsdp__additional_details_tab", { "uxpcore_obsdp__additional_details_tab--active": isActive }), onClick: (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (appendToURL) {
                                                if (isActive) {
                                                    navigate(generateURL({ removeParams: [urlParams.additionalDetailsTab] }));
                                                }
                                                else {
                                                    navigate(generateURL({ searchParams: { [urlParams.additionalDetailsTab]: adTab.id } }));
                                                }
                                            }
                                            else {
                                                setSelectedAdditionalDetailsTab(prev => prev === adTab.id ? null : adTab.id);
                                            }
                                        }, children: _jsx("div", { className: "uxpcore_obsdp__additional_details_tab-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(adTab.icon) }) }) }, i);
                                }), injectionsLoading && (_jsx("div", { className: "uxpcore_obsdp__additional_details_tab-loader", children: _jsx(LoadingSpinner, { spinnerOnly: true, styles: { padding: '0.5rem' } }) }))] })] })
                : null] });
});
/**
 * @export
 * A component that displays a details panel for a selected row, with a title, toolbar, and main/additional details sections.
 *
 * @example
 * ```
 * <ObjectDetailsPanel
 *   title="User Details"
 *   data={{ id: '1', name: 'John Doe' }}
 *   generalDetails={<div>General info</div>}
 *   idField="id"
 * />
 * ```
 *
 * @example
 * ```
 * <ObjectDetailsPanel
 *   title={(row) => `Details for ${row.name}`}
 *   data={async () => ({ id: '1', name: 'John Doe', email: 'john@example.com' })}
 *   generalDetails={(row) => ({ fields: [{ label: 'Name', value: row.name }, { label: 'Email', value: row.email }] })}
 *   toolbarItems={{
 *     left: [{ icon: 'edit', label: 'Edit', onClick: (e, row) => console.log('Edit', row) }],
 *     right: [{ icon: 'share', label: 'Share' }]
 *   }}
 *   otherDetails={<div>Additional info</div>}
 *   additionlDetails={[
 *     { id: 'notes', icon: 'note', label: 'Notes', content: 'Sample notes' },
 *     { id: 'history', icon: 'history', label: 'History', content: (row) => <div>History for {row.name}</div> }
 *   ]}
 *   showCloseButton={true}
 *   onClose={() => console.log('Panel closed')}
 * />
 * ```
 *
 * @category Data Display/Object Search/Details Panel
 */
export const ObjectDetailsPanel = memo(ObjectDetailsPanelBase);
